import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { CommandCenter } from '../components/CommandCenter';
import { 
  Loader2, Settings, Users, CreditCard, HelpCircle, Save, AlertCircle,
  UserPlus, Mail, Building2, Shield, Trash2, CheckCircle, XCircle
} from 'lucide-react';

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

const menuItems = [
  { icon: CommandCenter, label: 'Overview', id: 'overview' },
  { icon: Settings, label: 'Settings', id: 'settings' },
  { icon: Users, label: 'Team', id: 'team' },
  { icon: CreditCard, label: 'Billing', id: 'billing' },
  { icon: HelpCircle, label: 'Support', id: 'support' },
];

const industries = [
  'Technology',
  'Manufacturing',
  'Retail',
  'Healthcare',
  'Financial Services',
  'Professional Services',
  'Real Estate',
  'Construction',
  'Education',
  'Other'
];

interface TeamMember {
  id: string;
  email: string;
  full_name: string | null;
  company: string | null;
  role: 'admin' | 'member';
  status: 'active' | 'pending';
}

export const Dashboard = () => {
  const { profile, updateProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'error' | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState({
    full_name: '',
    company: '',
    industry: '',
    phone: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);

  useEffect(() => {
    const testConnection = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id')
          .limit(1);

        if (error) throw error;
        setConnectionStatus('connected');
      } catch (error) {
        console.error('Supabase connection error:', error);
        setConnectionStatus('error');
      } finally {
        setLoading(false);
      }
    };

    testConnection();
  }, []);

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        company: profile.company || '',
        industry: profile.industry || '',
        phone: profile.phone || ''
      });
    }
  }, [profile]);

  useEffect(() => {
    if (activeTab === 'team') {
      fetchTeamMembers();
    }
  }, [activeTab]);

  const fetchTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setTeamMembers(data as TeamMember[]);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);

    try {
      await updateProfile(formData);
      setSuccess(true);
    } catch (error) {
      setError('Failed to update profile. Please try again.');
      console.error('Profile update error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(false);
    setInviting(true);

    try {
      // In a real application, you would implement the invite logic here
      // For now, we'll simulate the invite process
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setInviteSuccess(true);
      setInviteEmail('');
      setInviteRole('member');
    } catch (error) {
      setInviteError('Failed to send invite. Please try again.');
      console.error('Invite error:', error);
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      // In a real application, you would implement the remove logic here
      // For now, we'll simulate the removal process
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setTeamMembers(prev => prev.filter(member => member.id !== memberId));
    } catch (error) {
      console.error('Error removing team member:', error);
    }
  };

  return (
    <div className="pt-24 px-4 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ...springConfig }}
          className="mb-16"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
                  Welcome back, {profile?.full_name || 'User'}
                </span>
              </h1>
              <p className="text-gray-400 text-lg">
                Your AI-powered business insights dashboard
              </p>
            </div>
            <div className="flex items-center gap-4">
              {loading ? (
                <div className="flex items-center gap-2 text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Testing connection...
                </div>
              ) : connectionStatus === 'connected' ? (
                <div className="text-green-400 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  Connected
                </div>
              ) : (
                <div className="text-red-400 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-red-400" />
                  Connection error
                </div>
              )}
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-12 gap-8">
          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, ...springConfig }}
            className="col-span-12 md:col-span-3 space-y-2"
          >
            {menuItems.map((item, index) => (
              <motion.button
                key={item.id}
                whileHover={{ x: 4 }}
                onClick={() => setActiveTab(item.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ${
                  activeTab === item.id
                    ? 'bg-amber-400/10 text-amber-400'
                    : 'text-gray-400 hover:text-amber-400 hover:bg-amber-400/5'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </motion.button>
            ))}
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2, ...springConfig }}
            className="col-span-12 md:col-span-9"
          >
            {activeTab === 'overview' && <CommandCenter />}
            {activeTab === 'settings' && (
              <div className="p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10">
                <h2 className="text-2xl font-bold text-white mb-6">Profile Settings</h2>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                  </div>
                )}

                {success && (
                  <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2 text-green-400">
                    <Save className="w-5 h-5" />
                    Profile updated successfully
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label htmlFor="full_name" className="block text-sm font-medium text-gray-300">
                        Full Name
                      </label>
                      <input
                        type="text"
                        id="full_name"
                        name="full_name"
                        value={formData.full_name}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
                        placeholder="John Doe"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="company" className="block text-sm font-medium text-gray-300">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
                        placeholder="Company Inc."
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="industry" className="block text-sm font-medium text-gray-300">
                        Industry
                      </label>
                      <select
                        id="industry"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300 appearance-none"
                      >
                        <option value="" disabled>Select your industry</option>
                        {industries.map(industry => (
                          <option key={industry} value={industry} className="bg-background text-white">
                            {industry}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
                        placeholder="+1 (555) 000-0000"
                      />
                    </div>
                  </div>

                  <motion.button
                    type="submit"
                    disabled={saving}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={springConfig}
                    className="px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        Save Changes
                      </>
                    )}
                  </motion.button>
                </form>
              </div>
            )}
            {activeTab === 'team' && (
              <div className="space-y-8">
                <div className="p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-white">Team Management</h2>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springConfig}
                      onClick={() => document.getElementById('inviteForm')?.scrollIntoView({ behavior: 'smooth' })}
                      className="px-4 py-2 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-sm hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300 flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      Invite Team Member
                    </motion.button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-amber-400/20">
                          <th className="py-3 text-left text-sm font-semibold text-gray-400">Member</th>
                          <th className="py-3 text-left text-sm font-semibold text-gray-400">Company</th>
                          <th className="py-3 text-left text-sm font-semibold text-gray-400">Role</th>
                          <th className="py-3 text-left text-sm font-semibold text-gray-400">Status</th>
                          <th className="py-3 text-right text-sm font-semibold text-gray-400">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-400/10">
                        {teamMembers.map((member) => (
                          <motion.tr
                            key={member.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ type: "spring", stiffness: 100, damping: 20 }}
                            className="group"
                          >
                            <td className="py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-amber-400/20 flex items-center justify-center">
                                  <Users className="w-4 h-4 text-amber-400" />
                                </div>
                                <div>
                                  <div className="font-medium text-white">{member.full_name}</div>
                                  <div className="text-sm text-gray-400">{member.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-4">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-300">{member.company || 'N/A'}</span>
                              </div>
                            </td>
                            <td className="py-4">
                              <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-amber-400" />
                                <span className="text-gray-300 capitalize">{member.role}</span>
                              </div>
                            </td>
                            <td className="py-4">
                              {member.status === 'active' ? (
                                <div className="flex items-center gap-2 text-green-400">
                                  <CheckCircle className="w-4 h-4" />
                                  <span>Active</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-amber-400">
                                  <XCircle className="w-4 h-4" />
                                  <span>Pending</span>
                                </div>
                              )}
                            </td>
                            <td className="py-4 text-right">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                transition={springConfig}
                                onClick={() => handleRemoveMember(member.id)}
                                className="text-gray-400 hover:text-red-400 transition-colors duration-200"
                              >
                                <Trash2 className="w-5 h-5" />
                              </motion.button>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div id="inviteForm" className="p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10">
                  <h3 className="text-xl font-bold text-white mb-6">Invite New Team Member</h3>
                  
                  {inviteError && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
                      <AlertCircle className="w-5 h-5" />
                      {inviteError}
                    </div>
                  )}

                  {inviteSuccess && (
                    <div className="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg flex items-center gap-2 text-green-400">
                      <Save className="w-5 h-5" />
                      Invitation sent successfully
                    </div>
                  )}

                  <form onSubmit={handleInvite} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                          Email Address
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
                          <input
                            type="email"
                            id="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            required
                            className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300"
                            placeholder="colleague@company.com"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="role" className="block text-sm font-medium text-gray-300">
                          Role
                        </label>
                        <div className="relative">
                          <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-400" />
                          <select
                            id="role"
                            value={inviteRole}
                            onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
                            className="w-full pl-10 pr-4 py-2 bg-amber-400/5 border border-amber-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 transition-colors duration-300 appearance-none"
                          >
                            <option value="member" className="bg-background text-white">Team Member</option>
                            <option value="admin" className="bg-background text-white">Administrator</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <motion.button
                      type="submit"
                      disabled={inviting}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      transition={springConfig}
                      className="px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {inviting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending Invite...
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-5 h-5" />
                          Send Invitation
                        </>
                      )}
                    </motion.button>
                  </form>
                </div>
              </div>
            )}
            {activeTab === 'billing' && (
              <div className="p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10">
                <h2 className="text-2xl font-bold text-white mb-4">Billing & Subscription</h2>
                <p className="text-gray-400">Manage your subscription and billing information</p>
              </div>
            )}
            {activeTab === 'support' && (
              <div className="p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10">
                <h2 className="text-2xl font-bold text-white mb-4">Support</h2>
                <p className="text-gray-400">Get help and support for your account</p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};